import { ActionContext, Module } from "vuex";
import { CognitoUser } from "@aws-amplify/auth";

import { RootState } from "@/store/root";
import { auth as manager } from "@/auth";
import { Modes } from "@/modes";
import { analytics } from "@/analytics";

export interface AuthState {
  enabled: boolean;
  user: any;
  raw: CognitoUser | null;
  groups: string[];
}

const enabled = process.env.VUE_APP_COGNITO_ENABLED === "1";

export const auth: Module<AuthState, RootState> = {
  state: {
    enabled: enabled,
    user: null,
    raw: null,
    groups: enabled ? [] : (process.env.VUE_APP_DEFAULT_GROUPS || "").split(" ")
  },
  namespaced: true,
  mutations: {
    UPDATE_USER(state: AuthState, user?: any) {
      if (state.enabled) {
        state.user = user || null;
      }
    },
    UPDATE_RAW(state: AuthState, raw: CognitoUser | null) {
      if (state.enabled) {
        state.raw = raw;
      }
    },
    UPDATE_GROUPS(state: AuthState, groups: string[]) {
      if (state.enabled) {
        state.groups = groups;
      }
    }
  },
  actions: {
    async update(
      context: ActionContext<AuthState, RootState>,
      raw: CognitoUser | null = null
    ) {
      try {
        context.commit("UPDATE_RAW", raw);

        const groups = await manager.groups();
        const user = await manager.user();

        if (analytics.enabled) {
          analytics.update({
            address: user.attributes.email,
            channelType: "EMAIL",
            optOut: "ALL",
            userId: user.attributes.sub,
            userAttributes: Object.assign(
              {},
              ...Object.entries(user.attributes).map(([key, value]) => ({
                [key]: !Array.isArray(value) ? [String(value)] : String(value)
              }))
            )
          });
        }

        context.commit("UPDATE_USER", user);
        context.commit("UPDATE_GROUPS", groups);
        context.commit(
          "UPDATE_MODE",
          Modes.MSA.check(groups)
            ? Modes.MSA
            : Modes.Lien.check(groups)
            ? Modes.Lien
            : Modes.Encode.check(groups)
            ? Modes.Encode
            : Modes.Analysis,
          { root: true }
        );
      } catch (exception) {
        context.commit("UPDATE_USER", null);
        context.commit("UPDATE_GROUPS", []);
      }
    },
    async logout(context: ActionContext<AuthState, RootState>) {
      try {
        await manager.signOut({ global: true });
        analytics.update({});
      } catch (exception) {
        console.log(exception);
      } finally {
        context.dispatch("clear", null, { root: true });
      }
    }
  }
};
